import { useCallback, useEffect, useRef, useState, useMemo, ReactNode } from 'react';

import { loaders, actions, Notification } from 'entities';

import { useAuth } from '../Auth';
import { useIsOnline } from 'hooks/online';
import { useRealTimeSubscription } from '../RealTime';
import { usePendingActions } from '../PendingActions';

import { getFirst, getLastItem, sortByProperty } from 'utils/array';
import { cascadeEval } from 'utils/conditions';

import { notificationContext } from './notification.context';
import { useSyncRef } from 'hooks';

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { loginAction } = useAuth();

  const isOnline = useIsOnline();
  const { refetchActions } = usePendingActions();

  const [loadingMoreNotifications, setLoadingMore] = useState(false);

  const [noMoreNotifications, setNoMoreNotifications] = useState(false);

  const notificationsRef = useRef<Notification[]>([]);

  const {
    overwrite,
    data: notifications,
    load,
  } = loaders.notifications.fetchNotifications.use({
    params: {
      target: 'future',
      lastDateReceived: getFirst(notificationsRef.current)?.timestamp,
    },

    defaultValue: [],

    blockLoad: !!loginAction || !isOnline,

    refreshMinutes: 20,

    reloadOnChangeRef: isOnline,

    parser: (notifications) => {
      const [mostRecent] = notificationsRef.current;

      const [recentReceived] = notifications;

      const merged = cascadeEval([
        { is: !mostRecent, then: notifications },
        { is: !notifications.length, then: notificationsRef.current },
        {
          is: mostRecent?.timestamp > recentReceived?.timestamp,
          then: [...notificationsRef.current, ...notifications],
        },
        { is: true, then: [...notifications, ...notificationsRef.current] },
      ]);

      notificationsRef.current = merged;

      return merged;
    },
  });

  const actionRef = useSyncRef({
    refetchActions,
  });

  const registerNewNotifications = useCallback(
    (newNotifications: Notification[]) => {
      actionRef.current.refetchActions();

      overwrite((old) => {
        const updated = sortByProperty([...newNotifications, ...old], 'timestamp', {
          timestamp: 'DESC',
        });

        notificationsRef.current = updated;

        return updated;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overwrite],
  );

  useRealTimeSubscription('NOTIFICATION', registerNewNotifications);

  useEffect(() => {
    if (!loginAction) return;

    overwrite([]);
    notificationsRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginAction, isOnline]);

  const fetchMoreOldNotifications = useCallback(async () => {
    setLoadingMore(true);

    const existingNotifications = notificationsRef.current;

    const newNotifications = await load({
      target: 'past',
      lastDateReceived: getLastItem(existingNotifications)?.timestamp,
    });

    setLoadingMore(false);

    if (!newNotifications?.length) setNoMoreNotifications(true);
  }, [load]);

  const { execute: markAsRead } = actions.notifications.markNotificationAsRead.use();

  const markNotificationAsRead = useCallback(
    async (unreadNotification: Notification) => {
      const { read, timestamp, id } = unreadNotification;

      if (read) return;

      const success = await markAsRead({
        timestamp,
      });

      if (!success) return;

      overwrite((old) =>
        old.map((notification) =>
          notification.id === id ? { ...notification, read: true } : notification,
        ),
      );
    },
    [markAsRead, overwrite],
  );

  const unreadNotifications = useMemo(() => {
    const unread = notifications.filter(({ read }) => !read);

    return unread.length;
  }, [notifications]);

  const data = useMemo(
    () => ({
      notifications,
      unreadNotifications,
      fetchMoreOldNotifications,
      markNotificationAsRead,
      loadingMoreNotifications,
      noMoreNotifications,
      registerNewNotifications,
    }),
    [
      notifications,
      unreadNotifications,
      fetchMoreOldNotifications,
      markNotificationAsRead,
      registerNewNotifications,
      loadingMoreNotifications,
      noMoreNotifications,
    ],
  );

  return <notificationContext.Provider value={data}>{children}</notificationContext.Provider>;
}
